import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Spin, message } from "antd";
import { ArrowLeftOutlined, PrinterOutlined, UpOutlined, GlobalOutlined } from "@ant-design/icons";
import { getTermsOfService } from "../api/legalApi";
import styles from "../assets/scss/LegalPages.module.scss";

const TermsOfService = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [termsData, setTermsData] = useState(null);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [language, setLanguage] = useState(searchParams.get("lang") || "uz");

  useEffect(() => {
    fetchTermsOfService();

    // Scroll listener for scroll-to-top button
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [language]);

  const fetchTermsOfService = async () => {
    try {
      setLoading(true);
      const response = await getTermsOfService(language);
      if (response.success) {
        setTermsData(response.data);
      } else {
        const errorMsg = language === "en"
          ? "Error loading data"
          : "Ma'lumotlarni yuklashda xatolik yuz berdi";
        setError(errorMsg);
        message.error(errorMsg);
      }
    } catch (err) {
      const errorMsg = language === "en"
        ? "Error loading data"
        : "Ma'lumotlarni yuklashda xatolik yuz berdi";
      setError(errorMsg);
      message.error(errorMsg);
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleLanguage = () => {
    const newLang = language === "uz" ? "en" : "uz";
    setLanguage(newLang);
    setSearchParams({ lang: newLang });
  };

  const handlePrint = () => {
    window.print();
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(`section-${sectionId}`);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setActiveSection(sectionId);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large" tip={language === "en" ? "Loading..." : "Yuklanmoqda..."} />
      </div>
    );
  }

  if (error || !termsData) {
    return (
      <div className={styles.legalContainer}>
        <div className={styles.errorContainer}>
          <h3>{language === "en" ? "Error" : "Xatolik"}</h3>
          <p>{error || (language === "en" ? "Data not found" : "Ma'lumotlar topilmadi")}</p>
          <button onClick={handleGoBack} className={styles.backButton}>
            <ArrowLeftOutlined />
            {language === "en" ? "Back" : "Orqaga"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.legalContainer}>
      <div className={styles.topActions}>
        <button onClick={handleGoBack} className={styles.backButton}>
          <ArrowLeftOutlined />
          {language === "en" ? "Back" : "Orqaga"}
        </button>

        <div className={styles.rightButtons}>
          <button onClick={toggleLanguage} className={styles.languageButton}>
            <GlobalOutlined />
            {language === "uz" ? "English" : "O'zbekcha"}
          </button>
          <button onClick={handlePrint} className={styles.printButton}>
            <PrinterOutlined />
            {language === "en" ? "Print" : "Chop etish"}
          </button>
        </div>
      </div>

      <div className={styles.header}>
        <h1>{termsData.title}</h1>
        <p className={styles.lastUpdated}>
          {language === "en" ? "Last updated: " : "Oxirgi yangilanish: "}
          {termsData.last_updated}
        </p>
      </div>

      <div className={styles.contentWrapper}>
        {/* Table of Contents */}
        <div className={styles.tableOfContents}>
          <h3>{language === "en" ? "Table of Contents" : "Mundarija"}</h3>
          <ul>
            {termsData.sections &&
              termsData.sections.map((section) => (
                <li key={section.id}>
                  <a
                    href={`#section-${section.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(section.id);
                    }}
                    className={activeSection === section.id ? 'active' : ''}
                  >
                    {section.heading}
                  </a>
                </li>
              ))}
          </ul>
        </div>

        {/* Main Content */}
        <div className={styles.content}>
          {termsData.sections &&
            termsData.sections.map((section) => (
              <div
                key={section.id}
                id={`section-${section.id}`}
                className={styles.section}
              >
                <h2>{section.heading}</h2>
                <p>{section.content}</p>
              </div>
            ))}
        </div>
      </div>

      {/* Scroll to Top Button */}
      <button
        onClick={scrollToTop}
        className={`${styles.scrollToTop} ${showScrollTop ? styles.visible : ''}`}
        aria-label={language === "en" ? "Scroll to top" : "Yuqoriga ko'tarish"}
      >
        <UpOutlined />
      </button>
    </div>
  );
};

export default TermsOfService;
